import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Phone,
  Radio,
  RefreshCcw,
  Search,
  Smartphone,
  Ticket,
  Zap,
} from 'lucide-react';
import { useAdminAPI } from '../hooks/useAdminAPI';
import {
  MtCenterTransaction,
  MtCenterTransactionKind,
  MtCenterTransactionsSummary,
} from '../types/api';

type KindFilter = MtCenterTransactionKind | 'all';
type StatusFilter = 'all' | 'completed' | 'pending' | 'failed' | 'cancelled';

const PAGE_SIZE = 25;

const kindLabels: Record<MtCenterTransactionKind, string> = {
  service: 'Servicio',
  tae: 'TAE',
};

const statusStyles: Record<string, string> = {
  completed: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  failed: 'bg-rose-100 text-rose-800 border-rose-200',
  cancelled: 'bg-slate-100 text-slate-700 border-slate-200',
};

const statusLabels: Record<string, string> = {
  completed: 'Completado',
  pending: 'Pendiente',
  failed: 'Fallido',
  cancelled: 'Cancelado',
};

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount || 0);

const formatDateTime = (input?: string) => {
  if (!input) return '—';
  try {
    return new Intl.DateTimeFormat('es-MX', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(input));
  } catch {
    return input;
  }
};

const getStatusClass = (status?: string) =>
  statusStyles[String(status || '').toLowerCase()] || 'bg-slate-100 text-slate-700 border-slate-200';

const getStatusLabel = (status?: string) => {
  const key = String(status || '').toLowerCase();
  return statusLabels[key] || status || '—';
};

const MtCenterTransactionsSection: React.FC = () => {
  const { getMtCenterTransactions, accessToken, isAuthenticated } = useAdminAPI();
  const [transactions, setTransactions] = useState<MtCenterTransaction[]>([]);
  const [summary, setSummary] = useState<MtCenterTransactionsSummary>({
    count: 0,
    totalAmount: 0,
    totalSurcharge: 0,
  });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(searchTerm.trim()), 350);
    return () => window.clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPage(0);
  }, [kindFilter, statusFilter, debouncedSearch, startDate, endDate]);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getMtCenterTransactions({
        kind: kindFilter,
        status: statusFilter === 'all' ? undefined : statusFilter,
        search: debouncedSearch || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setTransactions(response.transactions);
      setSummary(response.summary);
      setTotal(response.total);
    } catch (err) {
      console.error('Error fetching MT Center transactions:', err);
      setError(
        err instanceof Error ? err.message : 'No se pudieron cargar las transacciones de MT Center.'
      );
      setTransactions([]);
      setSummary({ count: 0, totalAmount: 0, totalSurcharge: 0 });
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [
    debouncedSearch,
    endDate,
    getMtCenterTransactions,
    kindFilter,
    page,
    startDate,
    statusFilter,
  ]);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) return;
    fetchTransactions();
  }, [accessToken, fetchTransactions, isAuthenticated]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const canGoPrev = page > 0;
  const canGoNext = page + 1 < totalPages;

  const serviceCount = useMemo(
    () => transactions.filter((tx) => tx.kind === 'service').length,
    [transactions]
  );
  const taeCount = useMemo(
    () => transactions.filter((tx) => tx.kind === 'tae').length,
    [transactions]
  );

  const kindTabs: { id: KindFilter; label: string }[] = [
    { id: 'all', label: 'Todos' },
    { id: 'service', label: 'Servicios' },
    { id: 'tae', label: 'TAE' },
  ];

  const statusTabs: { id: StatusFilter; label: string }[] = [
    { id: 'all', label: 'Cualquier estado' },
    { id: 'completed', label: 'Completado' },
    { id: 'pending', label: 'Pendiente' },
    { id: 'failed', label: 'Fallido' },
    { id: 'cancelled', label: 'Cancelado' },
  ];

  return (
    <div className="space-y-6">
      <header className="relative overflow-hidden rounded-2xl border border-[#d5f3f5] bg-gradient-to-br from-[#f0fdfd] via-white to-[#eef8ff] p-5 lg:p-6">
        <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#3bbcc8]/15 blur-2xl" />
        <div className="pointer-events-none absolute -bottom-16 right-20 h-36 w-36 rounded-full bg-violet-300/20 blur-2xl" />

        <div className="relative flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 text-xs font-medium text-[#0d9488] shadow-sm ring-1 ring-[#3bbcc8]/20">
              <Radio className="h-3.5 w-3.5" />
              MT Center · servicios y TAE
            </div>
            <h2 className="flex items-center gap-2 text-2xl font-semibold text-secondary-900">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[#3bbcc8] text-white shadow-sm">
                <Zap className="h-5 w-5" />
              </span>
              Pagos MT Center
            </h2>
            <p className="mt-1 max-w-2xl text-sm text-secondary-600 lg:text-base">
              Consulta pagos de servicios y recargas TAE procesados vía MT Center, con detalle de
              ticket, proveedor y comisiones.
            </p>
          </div>
          <button
            onClick={fetchTransactions}
            disabled={loading}
            className="inline-flex items-center gap-2 self-start rounded-lg border border-[#b6e8ec] bg-white px-4 py-2 text-secondary-700 shadow-sm transition-colors hover:bg-[#f0fdfd] disabled:opacity-50 lg:self-auto"
          >
            <RefreshCcw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
        </div>
      </header>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <div className="card relative overflow-hidden p-4">
          <div className="absolute right-0 top-0 h-16 w-16 rounded-bl-full bg-[#e6fafb]" />
          <p className="text-sm text-secondary-500">Transacciones</p>
          <p className="mt-1 text-2xl font-semibold text-secondary-900">{summary.count}</p>
          <p className="mt-1 text-xs text-secondary-400">
            En página: {serviceCount} servicios · {taeCount} TAE
          </p>
        </div>
        <div className="card p-4">
          <p className="text-sm text-secondary-500">Monto total</p>
          <p className="mt-1 text-2xl font-semibold text-secondary-900">
            {formatCurrency(summary.totalAmount)}
          </p>
          <p className="mt-1 text-xs text-secondary-400">Según filtros aplicados</p>
        </div>
        <div className="card p-4">
          <p className="text-sm text-secondary-500">Comisiones</p>
          <p className="mt-1 text-2xl font-semibold text-amber-700">
            {formatCurrency(summary.totalSurcharge)}
          </p>
          <p className="mt-1 text-xs text-secondary-400">Surcharge acumulado</p>
        </div>
        <div className="card p-4">
          <p className="text-sm text-secondary-500">Promedio por pago</p>
          <p className="mt-1 text-2xl font-semibold text-[#0d9488]">
            {formatCurrency(summary.count > 0 ? summary.totalAmount / summary.count : 0)}
          </p>
          <p className="mt-1 text-xs text-secondary-400">Monto / transacciones</p>
        </div>
      </section>

      <div className="card space-y-4 p-4">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="inline-flex self-start rounded-lg border border-secondary-200 bg-white p-1">
            {kindTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setKindFilter(tab.id)}
                className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                  kindFilter === tab.id
                    ? tab.id === 'tae'
                      ? 'bg-violet-100 text-violet-800'
                      : tab.id === 'service'
                      ? 'bg-sky-100 text-sky-800'
                      : 'bg-[#e6fafb] text-[#0d9488]'
                    : 'text-secondary-600 hover:bg-secondary-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex flex-1 flex-col gap-3 sm:flex-row xl:justify-end">
            <div className="relative flex-1 xl:max-w-xs">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Buscar teléfono, referencia, proveedor..."
                className="input-field pl-10"
              />
            </div>
            <div className="flex gap-2">
              <div className="relative">
                <Calendar className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400" />
                <input
                  type="date"
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                  className="input-field min-w-[150px] pl-10"
                  title="Fecha inicio"
                />
              </div>
              <div className="relative">
                <Calendar className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400" />
                <input
                  type="date"
                  value={endDate}
                  onChange={(event) => setEndDate(event.target.value)}
                  className="input-field min-w-[150px] pl-10"
                  title="Fecha fin"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {statusTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setStatusFilter(tab.id)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                statusFilter === tab.id
                  ? 'bg-secondary-900 text-white'
                  : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {error && (
          <div className="flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50 p-3 text-rose-700">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <p className="font-medium">Error al cargar transacciones MT Center</p>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}

        <div className="overflow-x-auto rounded-xl border border-secondary-100">
          <table className="min-w-full divide-y divide-secondary-200">
            <thead className="bg-secondary-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-secondary-500">
                  Tipo / Referencia
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-secondary-500">
                  Cliente
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-secondary-500">
                  Proveedor
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-secondary-500">
                  Monto
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-secondary-500">
                  Estado
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-secondary-500">
                  Fecha
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-100 bg-white">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12">
                    <div className="flex flex-col items-center gap-2 text-secondary-500">
                      <div className="h-7 w-7 animate-spin rounded-full border-b-2 border-[#3bbcc8]" />
                      <span className="text-sm">Cargando transacciones...</span>
                    </div>
                  </td>
                </tr>
              ) : transactions.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-secondary-500">
                    No hay pagos MT Center para los filtros seleccionados.
                  </td>
                </tr>
              ) : (
                transactions.map((tx) => {
                  const isExpanded = expandedId === tx.id;
                  const accountLabel =
                    tx.kind === 'tae'
                      ? tx.rechargePhone || tx.accountNumber
                      : tx.accountNumber || tx.rechargePhone;

                  return (
                    <React.Fragment key={tx.id}>
                      <tr
                        className="cursor-pointer transition-colors hover:bg-[#f8fffe]"
                        onClick={() => setExpandedId(isExpanded ? null : tx.id)}
                      >
                        <td className="whitespace-nowrap px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span
                              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                                tx.kind === 'tae'
                                  ? 'bg-violet-100 text-violet-800'
                                  : 'bg-sky-100 text-sky-800'
                              }`}
                            >
                              {tx.kind === 'tae' ? (
                                <Smartphone className="h-3 w-3" />
                              ) : (
                                <Zap className="h-3 w-3" />
                              )}
                              {kindLabels[tx.kind]}
                            </span>
                          </div>
                          <div className="mt-1 font-mono text-sm font-medium text-secondary-900">
                            {tx.reference || '—'}
                          </div>
                          <div className="text-xs text-secondary-400">ID: {tx.id.slice(0, 8)}…</div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <div className="text-sm font-semibold text-secondary-900">
                            {tx.userName || '—'}
                          </div>
                          {tx.phoneNumber && (
                            <div className="mt-0.5 flex items-center gap-1 text-xs text-secondary-500">
                              <Phone className="h-3 w-3" />
                              {tx.phoneNumber}
                            </div>
                          )}
                          {tx.email && (
                            <div className="max-w-[180px] truncate text-xs text-secondary-400">
                              {tx.email}
                            </div>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <div className="text-sm font-semibold text-secondary-900">
                            {tx.providerName || tx.providerId || '—'}
                          </div>
                          {accountLabel && (
                            <div className="mt-0.5 font-mono text-xs text-secondary-500">
                              {accountLabel}
                            </div>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <div className="text-lg font-semibold text-secondary-900">
                            {formatCurrency(tx.amount)}
                          </div>
                          {(tx.surcharge > 0 || tx.billAmount !== tx.amount) && (
                            <div className="text-xs text-secondary-500">
                              Recibo: {formatCurrency(tx.billAmount)}
                              {tx.surcharge > 0 && ` · Comisión: ${formatCurrency(tx.surcharge)}`}
                            </div>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <span
                            className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${getStatusClass(
                              tx.status
                            )}`}
                          >
                            {getStatusLabel(tx.status)}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-secondary-600">
                          {formatDateTime(tx.createdAt)}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-[#f8fffe]">
                          <td colSpan={6} className="px-4 py-4">
                            <div className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
                              <div className="rounded-lg border border-secondary-100 bg-white p-3">
                                <p className="mb-1 flex items-center gap-1 text-xs text-secondary-500">
                                  <Ticket className="h-3.5 w-3.5" />
                                  Ticket MT Center
                                </p>
                                <p className="font-mono text-secondary-900">
                                  {tx.mtCenterTicket || '—'}
                                </p>
                              </div>
                              <div className="rounded-lg border border-secondary-100 bg-white p-3">
                                <p className="mb-1 text-xs text-secondary-500">No. transacción MT</p>
                                <p className="font-mono text-secondary-900">
                                  {tx.mtCenterTransaction || '—'}
                                </p>
                              </div>
                              <div className="rounded-lg border border-secondary-100 bg-white p-3">
                                <p className="mb-1 text-xs text-secondary-500">Descripción</p>
                                <p className="text-secondary-900">{tx.description || '—'}</p>
                              </div>
                              <div className="rounded-lg border border-secondary-100 bg-white p-3">
                                <p className="mb-1 text-xs text-secondary-500">SKU / proveedor</p>
                                <p className="font-mono text-secondary-900">
                                  {tx.providerId || '—'}
                                </p>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {total > 0 && (
          <div className="flex flex-col gap-3 pt-1 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-secondary-500">
              Mostrando {page * PAGE_SIZE + 1}–
              {Math.min((page + 1) * PAGE_SIZE, total)} de {total}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={!canGoPrev || loading}
                className="inline-flex items-center gap-1 rounded-lg border border-secondary-200 px-3 py-1.5 text-sm text-secondary-600 hover:bg-secondary-50 disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" />
                Anterior
              </button>
              <span className="px-2 text-sm text-secondary-600">
                {page + 1} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={!canGoNext || loading}
                className="inline-flex items-center gap-1 rounded-lg border border-secondary-200 px-3 py-1.5 text-sm text-secondary-600 hover:bg-secondary-50 disabled:opacity-40"
              >
                Siguiente
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MtCenterTransactionsSection;
